import { useSelector } from 'react-redux';
import {
  getRawNoticeUrl,
  NoticeRecord,
  selectNoticeRecords,
} from '../notices/noticeSlice';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fromLonLat } from 'ol/proj';
import { MapTooltip } from './MapTooltip';
import process from 'process';
import { selectFilteredTsps } from './slice/mapSelectors';
import { selectShowNoticeMarkers } from '../plotter/plotterSelectors';
import { FeatureLike } from '../utils/geoJsonUtils';
import { useMapLayer } from '../hooks/useMapLayer';
import { useMapContext, MapEventListener } from './context/MapContext';

const PUBLIC_FOLDER = process.env.PUBLIC_URL;

const getStyle = () => {
  return {
    'icon-src': `${PUBLIC_FOLDER}/marker-icon-2x.png`,
    'icon-scale': 0.5,
    'icon-displacement': [0, 24],
  };
};

export type MapNoticesProps = {
  zIndex: number;
};

export const MapNotices: React.FC<MapNoticesProps> = ({ zIndex }) => {
  const {
    registerHoverListener,
    unregisterHoverListener,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();
  const notices = useSelector(selectNoticeRecords);
  const includedTsps = useSelector(selectFilteredTsps);
  const showNoticeMarkers = useSelector(selectShowNoticeMarkers);

  const [tooltipNotice, setTooltipNotice] = useState<NoticeRecord | null>(null);

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (feature?.getProperties()?.key === `notice-${tooltipNotice?.id}`)
        return;
      if (!feature || !feature?.getProperties()?.key?.startsWith('notice-')) {
        setTooltipNotice(null);
        return;
      }
      const noticeId = Number(feature.getProperties()?.id);
      const notice = notices.find((notice) => notice.id === noticeId) ?? null;
      setTooltipNotice(notice);
    },
    [notices],
  );

  const clickListener: MapEventListener = useCallback(
    (feature) => {
      const noticeId =
        feature && feature.getProperties()?.key?.startsWith('notice-')
          ? feature.getProperties()?.id
          : null;
      const notice = notices.find((notice) => notice.id === noticeId);
      if (!notice) return;
      const noticeUrl = getRawNoticeUrl(notice);
      if (noticeUrl.endsWith('.pdf') || noticeUrl.endsWith('.html')) {
        window.open(noticeUrl, '_blank');
      }
    },
    [notices],
  );

  useEffect(() => {
    registerHoverListener(hoverListener);
    return () => {
      unregisterHoverListener(hoverListener);
    };
  }, [hoverListener]);

  useEffect(() => {
    registerClickListener(clickListener);
    return () => {
      unregisterClickListener(clickListener);
    };
  }, [clickListener]);

  const filteredNotices = useMemo(() => {
    return notices.filter((notice) => {
      if (includedTsps.length === 0) {
        return true;
      }
      return includedTsps.includes(notice.tsp);
    });
  }, [notices, includedTsps]);

  const _features = useMemo<FeatureLike[]>(
    () =>
      filteredNotices
        .flatMap((notice) => {
          return notice.nodes.map((position) => {
            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: fromLonLat([position[1], position[0]]),
              },
              properties: {
                id: notice.id,
                key: `notice-${notice.id}`,
              },
            } as FeatureLike;
          });
        })
        .sort(
          (a, b) =>
            (b.geometry.coordinates[1] as number) -
            (a.geometry.coordinates[1] as number),
        ),
    [filteredNotices],
  );

  const features = useMemo(() => {
    return showNoticeMarkers ? _features : [];
  }, [_features, showNoticeMarkers]);

  const style = useMemo(() => getStyle(), []);

  useMapLayer(features, { zIndex, style });

  const tooltipNoticeRecord = useMemo(
    () => tooltipNotice?.best_available_notice_record ?? null,
    [tooltipNotice],
  );

  return (
    <>
      <MapTooltip visible={Boolean(tooltipNoticeRecord)}>
        {tooltipNoticeRecord && (
          <div className="bg-dark p-2 px-3 border-dark text-light rounded">
            <p className="fs-4">{tooltipNoticeRecord.identifier}</p>
            {tooltipNoticeRecord.reason && (
              <div
                style={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: '10',
                  WebkitBoxOrient: 'vertical',
                  width: '300px',
                  whiteSpace: 'pre-line',
                }}
              >
                <b>Full Text</b>:
                {tooltipNoticeRecord.reason.replace(/\n+/g, '\n')}
              </div>
            )}
            {tooltipNoticeRecord.effective_datetime && (
              <div>
                <b>Date</b>:{' '}
                {new Date(
                  tooltipNoticeRecord.effective_datetime,
                ).toLocaleDateString()}
              </div>
            )}
            <div>
              <b>Amount</b>: {tooltipNoticeRecord.capacity ?? '--'}
            </div>
            <div>
              <b>Tsp</b>: {tooltipNoticeRecord.tsp_name ?? '--'}
            </div>
            <div>
              <b>Percent</b>: {tooltipNoticeRecord.percentage ?? '--'}
            </div>
            <div>
              <b>Priority</b>: {tooltipNoticeRecord.priority ?? '--'}
            </div>
          </div>
        )}
      </MapTooltip>
    </>
  );
};
